import { useState } from 'react';
import { logEvent } from '@amplitude/analytics-browser';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '../Typography';
import Button from '../Button';
import CheckboxField from '../Field/CheckboxField';
import Modal from '../../componentsv2/Modal';
import { startCompliance } from '../../services/compliance';
import { useAppDispatch, useAppSelector, setProductStatus } from '../../store';
import { useLazyTranslation } from '../../hooks/i18n';
import { filesURL } from '../../constants/auth';
import { complianceTermsEvents } from '../../constants/events';
import { Nullable } from '../../types';
import { COLORS } from '../../themev2';

interface ComplianceTermsModalProps {
  isOpen: boolean;
}

const ComplianceTermsModal = ({ isOpen }: ComplianceTermsModalProps) => {
  const selectedCompany = useAppSelector((state) => state.companyUser.companyUser?.selectedCompany);
  const { t, isLoading: isLoadingTranslations } = useLazyTranslation(['compliance']);
  const dispatch = useAppDispatch();
  const [isAccepted, setIsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Nullable<string>>(null);
  const [hasClickedLink, setHasClickedLink] = useState(false);

  const agreementDocumentUrl = `${filesURL}/${t('compliance:termsModal.agreementDocument')}`;

  const handleLinkClick = () => {
    setHasClickedLink(true);
    logEvent(complianceTermsEvents.CLICK_TERMS_LINK, {
      'Document URL': agreementDocumentUrl,
    });
  };

  const handleAcceptanceChange = (checked: boolean) => {
    if (!hasClickedLink) {
      return;
    }
    setIsAccepted(checked);
    setError(null);
  };

  const handleConfirm = async () => {
    const productStatusId = selectedCompany?.productStatus?.id;
    if (!isAccepted || !selectedCompany?.id || !productStatusId) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const updatedProductStatus = await startCompliance(selectedCompany.id, productStatusId);
      dispatch(setProductStatus(updatedProductStatus));
      logEvent(complianceTermsEvents.CONFIRM_TERMS, {
        'Document URL': agreementDocumentUrl,
      });
      setIsAccepted(false);
    } catch (err: any) {
      setError(
        err?.response?.data?.message || err?.message || t('compliance:termsModal.errors.submissionFailed')
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingTranslations) return null;

  return (
    <Modal
      isOpen={isOpen}
      handleClose={() => {
        // Modal is non-dismissible, so this should not be called
      }}
      displayCloseButton={false}
      disableBackdropClick
      disableEscapeKeyDown
      size="medium"
      isAutoHeight
    >
      <Grid container rowSpacing={3}>
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '1rem' }}>
            {t('compliance:termsModal.title')}
          </Typography>
          <Typography variant="body1" sx={{ textAlign: 'left', color: COLORS.gray[1100] }}>
            {t('compliance:termsModal.description')}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Box
            sx={{
              border: `1px solid ${COLORS.indigo[100]}`,
              backgroundColor: COLORS.indigo[50],
              borderRadius: '8px',
              padding: '1rem',
            }}
          >
            <CheckboxField
              label={
                <>
                  {t('compliance:termsModal.checkboxLabelPrefix')}{' '}
                  <a
                    href={agreementDocumentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: COLORS.indigo[500], fontWeight: 600 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLinkClick();
                    }}
                  >
                    {t('compliance:termsModal.checkboxLabelLink')}
                  </a>
                  {t('compliance:termsModal.checkboxLabelSuffix')}
                </>
              }
              value={isAccepted}
              onChange={handleAcceptanceChange}
              error={error || undefined}
              disabled={isLoading || !hasClickedLink}
              tooltip={!hasClickedLink ? t('compliance:termsModal.checkboxTooltip') : undefined}
            />
          </Box>
        </Grid>
        {error && (
          <Grid item xs={12}>
            <Typography sx={{ color: COLORS.feedback.danger, fontSize: '14px' }}>{error}</Typography>
          </Grid>
        )}
        <Grid item xs={12}>
          <Button
            fullWidth
            color="secondary"
            onClick={handleConfirm}
            isLoading={isLoading}
            disabled={!isAccepted || isLoading}
          >
            {t('compliance:termsModal.confirmButton')}
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default ComplianceTermsModal;
